import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Users, FileText, Mail, Phone } from 'lucide-react';
import SabiwabeeLogo from '../components/SabiwabeeLogo';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-2 text-gray-900 hover:text-blue-600 transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Home</span>
              </Link>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-md transform hover:scale-105 transition-transform duration-300">
                <SabiwabeeLogo className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">Sabiwabee</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            </div>
            <p className="text-gray-600 text-lg">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              At Sabiwabee, we are committed to protecting your privacy and ensuring the security of your personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
              AI-powered mental health platform.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using Sabiwabee, you agree to the collection and use of information in accordance with this policy. 
              We are committed to transparency and will never sell your personal data to third parties.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <Lock className="w-5 h-5 mr-2 text-blue-600" />
                  Personal Information
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                  <li>Name, email address, and contact information</li>
                  <li>Date of birth and demographic information</li>
                  <li>Account credentials and profile information</li>
                  <li>Payment and billing information (processed securely through third-party providers)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-blue-600" />
                  Health and Wellness Data
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                  <li>Mental health assessments and responses</li>
                  <li>Journal entries and mood tracking data</li>
                  <li>Meditation and mindfulness session records</li>
                  <li>Progress tracking and analytics data</li>
                  <li>Chat conversations with AI therapists</li>
                  <li>Community forum interactions</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-blue-600" />
                  Usage Information
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                  <li>Device information and IP addresses</li>
                  <li>Browser type and operating system</li>
                  <li>Usage patterns and feature interactions</li>
                  <li>App performance and error logs</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                <li>Provide personalized AI therapy and mental health support</li>
                <li>Generate insights and recommendations based on your data</li>
                <li>Track your progress and mental health journey</li>
                <li>Improve our AI algorithms and platform features</li>
                <li>Ensure platform security and prevent fraud</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Send important updates and notifications</li>
                <li>Comply with legal obligations and regulations</li>
              </ul>
            </div>
          </section>

          {/* Data Security */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <Shield className="w-5 h-5 mr-2 text-blue-600" />
                Our Security Measures
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                <li>End-to-end encryption for all sensitive data</li>
                <li>HIPAA-compliant data storage and processing</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Secure cloud infrastructure with industry-standard protocols</li>
                <li>Access controls and authentication measures</li>
                <li>Regular data backups and disaster recovery procedures</li>
                <li>Employee training on data protection and privacy</li>
              </ul>
            </div>
          </section>

          {/* Data Sharing */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
              <li><strong>With your consent:</strong> When you explicitly authorize us to share your information</li>
              <li><strong>Service providers:</strong> With trusted third-party services that help us operate our platform (e.g., cloud storage, payment processing)</li>
              <li><strong>Legal requirements:</strong> When required by law or to protect our rights and safety</li>
              <li><strong>Emergency situations:</strong> When necessary to prevent harm to you or others</li>
              <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Privacy Rights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Access and Control</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm">
                  <li>Access your personal data</li>
                  <li>Update or correct your information</li>
                  <li>Delete your account and data</li>
                  <li>Export your data</li>
                </ul>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Communication Preferences</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm">
                  <li>Opt out of marketing communications</li>
                  <li>Control notification settings</li>
                  <li>Manage data sharing preferences</li>
                  <li>Request data processing restrictions</li>
                </ul>
              </div>
            </div>
          </section>

          {/* AI and Data Processing */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">AI and Data Processing</h2>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Transparent AI Processing</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our AI systems process your data to provide personalized mental health support. We are committed to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
                <li>Explaining how AI makes decisions about your care</li>
                <li>Providing transparency about data usage in AI training</li>
                <li>Ensuring AI recommendations are safe and appropriate</li>
                <li>Maintaining human oversight of AI systems</li>
                <li>Allowing you to opt out of AI processing features</li>
              </ul>
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain your information for as long as necessary to provide our services and comply with legal obligations:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-6">
              <li><strong>Active accounts:</strong> Data is retained while your account is active</li>
              <li><strong>Inactive accounts:</strong> Data may be deleted after 2 years of inactivity</li>
              <li><strong>Legal requirements:</strong> Some data may be retained longer for legal compliance</li>
              <li><strong>Deletion requests:</strong> Data is permanently deleted within 30 days of deletion requests</li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed">
                Sabiwabee is not intended for children under 13 years of age. We do not knowingly collect personal information 
                from children under 13. If you are a parent or guardian and believe your child has provided us with personal 
                information, please contact us immediately.
              </p>
            </div>
          </section>

          {/* International Users */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">International Users</h2>
            <p className="text-gray-700 leading-relaxed">
              If you are accessing Sabiwabee from outside the United States, please be aware that your information may be 
              transferred to, stored, and processed in the United States where our servers are located. By using our service, 
              you consent to the transfer of your information to the United States.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
              Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy 
              Policy periodically for any changes.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">privacy@sabiwabee.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">1-800-SABIWABEE</span>
                </div>
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700">Data Protection Officer: privacy@sabiwabee.com</span>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-8 text-center">
            <p className="text-gray-600">
              This Privacy Policy is effective as of {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })} and will remain in effect except with respect to any changes in its provisions in the future.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy; 